import React from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { FormProvider as HooksFormProvider } from 'react-hook-form';
import type { UseFormReturn } from 'react-hook-form/dist/types';
import type { FieldValues } from 'react-hook-form/dist/types/fields';

import { generateDataTestId } from '@/lib/tests';
import type { WithChildren } from '@/lib/types';

export interface FormProviderProps<
  TFieldValues extends FieldValues = any,
  TContext = any,
>
  extends WithChildren, UseFormReturn<TFieldValues, TContext> {
  background?: string;
  className?: string;
  formProps?: Omit<
    React.DetailedHTMLProps<
      React.FormHTMLAttributes<HTMLFormElement>,
      HTMLFormElement
    >,
    'onSubmit'
  >;
  name: string;
  onSubmit: SubmitHandler<TFieldValues>;
}

const trimTextFields = <T extends Record<string, any>>(
  data: T,
  excludeFields: string[] = [],
): T => {
  if (typeof data === 'object') {
    const trimmedData = {} as T;
    for (const key in data) {
      if (Object.hasOwn(data, key)) {
        const value = data[key];
        if (typeof value === 'string' && !excludeFields.includes(key)) {
          trimmedData[key] = value.trim();
        } else {
          trimmedData[key] = value;
        }
      }
    }
    return trimmedData;
  }
  return data;
};

const getPasswordFields = (data: any) => {
  return typeof data === 'object' && data !== null
    ? Object.keys(data).filter((fieldName) => {
        const fieldElement = document.querySelector(`[name="${fieldName}"]`);
        return fieldElement?.getAttribute('type') === 'password';
      })
    : [];
};

/**
 * Simple wrapper to avoid manually adding RHF FormProvider & required form.
 */
export const FormProvider = ({
  background,
  children,
  className,
  formProps,
  name,
  onSubmit,
  ...providerProps
}: FormProviderProps) => {
  const handleSubmit: SubmitHandler<any> = (data, event) => {
    const passwordFields = getPasswordFields(data);
    const trimmedData = trimTextFields(data, passwordFields);
    return onSubmit(trimmedData, event);
  };

  return (
    <HooksFormProvider {...providerProps}>
      <form
        data-test-id='FormProviderForm'
        style={{ width: '100%', background }}
        {...generateDataTestId('form', name)}
        name={name}
        {...formProps}
        className={className}
        method={formProps?.method ?? 'post'}
        noValidate
        onSubmit={providerProps.handleSubmit(handleSubmit, (err) =>
          console.log(err),
        )}
      >
        {children}
      </form>
    </HooksFormProvider>
  );
};
